/**
 * Jitter buffer for remote poses: renders 100 ms behind the newest sample, interpolating
 * between samples, and extrapolates head motion briefly when packets stop arriving.
 */
import * as THREE from 'three';
import { POSE_INTERPOLATION_MS, type PoseData, type PoseHand } from '@clinspace/shared';

export interface SampledHand {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  kind: PoseHand['kind'];
  pinch: number;
  curl: [number, number, number, number, number];
}

export interface SampledPose {
  headPosition: THREE.Vector3;
  headQuaternion: THREE.Quaternion;
  hands: [SampledHand | null, SampledHand | null];
  moving: PoseData['moving'];
  speaking: boolean;
  height: number;
  /** Seconds since the last packet arrived. */
  age: number;
}

interface Entry {
  receivedAt: number;
  pose: PoseData;
}

const MAX_EXTRAPOLATION_MS = 150;
const MAX_ENTRIES = 12;

const _pa = new THREE.Vector3();
const _pb = new THREE.Vector3();
const _qa = new THREE.Quaternion();
const _qb = new THREE.Quaternion();

function toHand(h: PoseHand): SampledHand {
  return {
    position: new THREE.Vector3(h.p[0], h.p[1], h.p[2]),
    quaternion: new THREE.Quaternion(h.q[0], h.q[1], h.q[2], h.q[3]),
    kind: h.kind,
    pinch: h.pinch,
    curl: [...h.curl] as SampledHand['curl'],
  };
}

function lerpHand(a: PoseHand | null, b: PoseHand | null, t: number): SampledHand | null {
  if (!a && !b) return null;
  if (!a) return toHand(b!);
  if (!b) return toHand(a);
  const out = toHand(a);
  _pb.set(b.p[0], b.p[1], b.p[2]);
  out.position.lerp(_pb, t);
  _qb.set(b.q[0], b.q[1], b.q[2], b.q[3]);
  out.quaternion.slerp(_qb, t);
  out.pinch = THREE.MathUtils.lerp(a.pinch, b.pinch, t);
  for (let i = 0; i < 5; i++) out.curl[i] = THREE.MathUtils.lerp(a.curl[i]!, b.curl[i]!, t);
  out.kind = t < 0.5 ? a.kind : b.kind;
  return out;
}

export class PoseBuffer {
  private entries: Entry[] = [];
  /** Estimated send interval, used for extrapolation velocity. */
  private velocity = new THREE.Vector3();

  push(pose: PoseData, now = performance.now()): void {
    const last = this.entries[this.entries.length - 1];
    if (last && pose.t <= last.pose.t) return; // out of order
    this.entries.push({ receivedAt: now, pose });
    if (last) {
      const dt = Math.max(1, pose.t - last.pose.t) / 1000;
      _pa.set(last.pose.head[0], last.pose.head[1], last.pose.head[2]);
      _pb.set(pose.head[0], pose.head[1], pose.head[2]);
      this.velocity.subVectors(_pb, _pa).divideScalar(dt);
      if (pose.moving === 2) this.velocity.set(0, 0, 0);
    }
    if (this.entries.length > MAX_ENTRIES)
      this.entries.splice(0, this.entries.length - MAX_ENTRIES);
  }

  get latest(): PoseData | undefined {
    return this.entries[this.entries.length - 1]?.pose;
  }

  get size(): number {
    return this.entries.length;
  }

  /** Pose to render now, or undefined when nothing has arrived yet. */
  sample(now = performance.now()): SampledPose | undefined {
    if (this.entries.length === 0) return undefined;
    const renderAt = now - POSE_INTERPOLATION_MS;
    const newest = this.entries[this.entries.length - 1]!;
    const age = (now - newest.receivedAt) / 1000;

    // Find the two entries bracketing renderAt by receive time.
    let before = this.entries[0]!;
    let after: Entry | undefined;
    for (let i = 0; i < this.entries.length; i++) {
      const e = this.entries[i]!;
      if (e.receivedAt <= renderAt) before = e;
      else {
        after = e;
        break;
      }
    }

    let t = 0;
    let a = before;
    let b = after ?? before;
    if (after && after !== before) {
      t = THREE.MathUtils.clamp(
        (renderAt - before.receivedAt) / Math.max(1, after.receivedAt - before.receivedAt),
        0,
        1,
      );
    } else if (!after) {
      // Beyond the newest sample: extrapolate head position a little.
      a = newest;
      b = newest;
    }

    const headPosition = new THREE.Vector3(a.pose.head[0], a.pose.head[1], a.pose.head[2]);
    _pb.set(b.pose.head[0], b.pose.head[1], b.pose.head[2]);
    headPosition.lerp(_pb, t);
    const headQuaternion = new THREE.Quaternion(
      a.pose.head[3],
      a.pose.head[4],
      a.pose.head[5],
      a.pose.head[6],
    );
    _qa.set(b.pose.head[3], b.pose.head[4], b.pose.head[5], b.pose.head[6]);
    headQuaternion.slerp(_qa, t);

    if (!after) {
      const overshoot =
        Math.min(MAX_EXTRAPOLATION_MS, Math.max(0, renderAt - newest.receivedAt)) / 1000;
      if (newest.pose.moving === 1) headPosition.addScaledVector(this.velocity, overshoot);
    }

    return {
      headPosition,
      headQuaternion,
      hands: [
        lerpHand(a.pose.hands[0], b.pose.hands[0], t),
        lerpHand(a.pose.hands[1], b.pose.hands[1], t),
      ],
      moving: b.pose.moving,
      speaking: b.pose.speaking,
      height: b.pose.height,
      age,
    };
  }

  clear(): void {
    this.entries = [];
    this.velocity.set(0, 0, 0);
  }
}
